import { type CatalogMaterial, type VendorCatalog } from '@api/vendor-portal';
import { createVendorMaterialMapping } from '@api/vendors';
import { Alert, Button, Group, Modal, NumberInput, Select, Stack, Text } from '@mantine/core';
import { PlusIcon } from '@phosphor-icons/react';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { useQueryClient } from '@tanstack/react-query';
import { type FC, useState } from 'react';

import AddVendorModal from '@components/containers/EntityMaster/AddVendorModal';
import { readApiError } from '@utils/apiError';

interface AddSupplierModalProps {
    /** The item a supplier is being added for. Mounted only while one is. */
    material: CatalogMaterial;
    /** Every vendor the brand has - the catalogue already holds them. */
    suppliers: VendorCatalog['suppliers'];
    /** A vendor the row already names - a pasted file's case - so confirming is all that is left. */
    initialVendorId?: string;
    /** What was typed into the row's picker, so nobody types the name twice. */
    initialSearch?: string;
    onClose: () => void;
    /** Fired once the mapping exists and the catalogue has been re-read. */
    onAdded: (vendorId: string) => void;
}

/**
 * The way out of a dead end, next to the dead end.
 *
 * The supplier picker only offers who the brand has mapped to an item, so goods from anybody
 * else used to stop the receipt until someone went to Settings. This maps a vendor to the item
 * on the spot - an existing one, or a new one through the same Add vendor form Settings uses.
 */
const AddSupplierModal: FC<AddSupplierModalProps> = ({
    material,
    suppliers,
    initialVendorId,
    initialSearch = '',
    onClose,
    onAdded,
}) => {
    const queryClient = useQueryClient();
    const [vendorId, setVendorId] = useState<string | null>(initialVendorId ?? null);
    const [search, setSearch] = useState(initialSearch);
    const [rate, setRate] = useState<string | number>('');
    const [isCreatingVendor, setIsCreatingVendor] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    // Both doors read the same catalogue under their own key; whichever is open re-reads.
    const refreshCatalog = () =>
        queryClient.invalidateQueries({
            predicate: (query) => query.queryKey[1] === 'vendor-catalog',
        });

    const handleSubmit = async () => {
        if (!vendorId) return;

        setSaving(true);
        setSaveError(null);
        try {
            await createVendorMaterialMapping({
                materialId: material.itemId,
                vendorId,
                cost: rate === '' ? undefined : Number(rate),
            });
            // The picker and the row check both read the catalogue, so it has to know about
            // the mapping before the row is told to select it.
            await refreshCatalog();
            onAdded(vendorId);
            onClose();
        } catch (err) {
            setSaveError(readApiError(err, 'Could not add the supplier. Please try again.'));
            setSaving(false);
        }
    };

    const options = suppliers
        .filter((supplier) => !material.supplierIds.includes(supplier.vendorId))
        .map((supplier) => ({ value: supplier.vendorId, label: supplier.name }));

    return (
        <>
            <Modal
                opened
                onClose={onClose}
                title="Add a supplier"
                size="md"
                centered
            >
                <Stack gap="md">
                    <Text size="sm" c="dimmed">
                        for {material.name || material.itemId}
                    </Text>

                    <Select
                        label="Supplier"
                        description="One of your vendors — it will be mapped to this item"
                        placeholder="Search vendors"
                        data={options}
                        value={vendorId}
                        onChange={setVendorId}
                        searchValue={search}
                        onSearchChange={setSearch}
                        nothingFoundMessage="No vendor by that name — add a new one below"
                        withAsterisk
                        searchable
                    />

                    <NumberInput
                        label="Agreed rate"
                        description={`Per ${material.unit || 'unit'}, in your own currency. Optional — leave blank if not known yet.`}
                        placeholder="49.50"
                        value={rate}
                        onChange={(value) => setRate(value === '' ? '' : Number(value))}
                        min={0}
                        decimalScale={2}
                        allowNegative={false}
                    />

                    {saveError && (
                        <Alert color="red" variant="light">
                            <Text size="xs">{saveError}</Text>
                        </Alert>
                    )}

                    <Group justify="space-between" gap="sm">
                        <Button
                            variant={ButtonVariant.DEFAULT}
                            leftSection={<PlusIcon size={14} />}
                            onClick={() => setIsCreatingVendor(true)}
                        >
                            New vendor
                        </Button>
                        <Group gap="sm">
                            <Button variant={ButtonVariant.DEFAULT} onClick={onClose}>
                                Cancel
                            </Button>
                            <Button loading={saving} disabled={!vendorId} onClick={handleSubmit}>
                                Add supplier
                            </Button>
                        </Group>
                    </Group>
                </Stack>
            </Modal>

            <AddVendorModal
                opened={isCreatingVendor}
                onClose={() => setIsCreatingVendor(false)}
                // Only a name nobody on the list answers to; a picked vendor's name is not a new one.
                initialName={vendorId ? undefined : search.trim() || undefined}
                initialCapabilities={['RM_SUPPLIER']}
                onCreated={(vendor) => {
                    // Re-read first, so the new vendor is in the list before it is selected.
                    void refreshCatalog().then(() => setVendorId(vendor.vendorId));
                }}
            />
        </>
    );
};

export default AddSupplierModal;
